import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Calendar, ArrowUpDown } from "lucide-react";
import { useExplainerController } from "./useExplainerController";
import { ReportingPeriodTimeline } from "../reporting-periods";
import { PeriodSelector } from "../holdings/components/PeriodSelector";
import { ExplainerTable, PeriodSummary } from "./components";

interface ExplainerProps {
  className?: string;
}



export const ExplainerPage: React.FC<ExplainerProps> = ({ className }) => {
  const controller = useExplainerController();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  
  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  // Category-based color function accessible to all components
  const getCategoryBasedValueColor = (value: number, category?: string) => {
    if (value === 0) return "text-gray-600";
    
    // Category-based coloring
    if (category === 'income') {
      // Income should always be green (increases in value)
      return "text-emerald-700 font-semibold";
    } else if (category === 'expenses') {
      // Expenses should always be red (decreases in value)
      return "text-red-700 font-semibold";
    } else {
      // For other categories (transactions, cash), use traditional pos/neg logic
      if (value > 0) return "text-emerald-700 font-semibold";
      if (value < 0) return "text-red-700 font-semibold";
      return "text-gray-600";
    }
  };

  const groupedData = controller.getGroupedTransactionData();

  if (!controller.selectedFund) {
    return (
      <Card className="m-6">
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Fund Selected</h3>
            <p className="text-gray-500">Please select a fund to view the explainer.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`${className}`}>
      <ReportingPeriodTimeline />
      <Card className="m-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowUpDown className="h-5 w-5" />
            Fund Transaction Explainer
          </CardTitle>
          <p className="text-sm text-gray-600">
            View transactions grouped by category with period-over-period comparison
          </p>
        </CardHeader>
        <CardContent>
          {/* Comparison Period Selector */}
          <div className="mb-6 max-w-md">
            <PeriodSelector
              selectedPeriod={controller.comparisonPeriod}
              onSelectPeriod={controller.setComparisonPeriod}
              onClearSelection={() => controller.setComparisonPeriod(null)}
              label="Comparison Period"
              allowEmpty={true}
            />
            {!controller.comparisonPeriod && (
              <p className="text-xs text-gray-500 mt-1">
                When no comparison period is selected, the previous period will be used automatically.
              </p>
            )}
          </div>

          {controller.isLoading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : !groupedData ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Reporting Period Selected</h3>
                <p className="text-gray-500">Please select a reporting period to view transactions.</p>
              </div>
            </div>
          ) : (
            <>
              <ExplainerTable
                selectedFund={controller.selectedFund}
                selectedPeriod={controller.selectedPeriod || null}
                comparisonPeriod={controller.comparisonPeriod || null}
                groupedData={groupedData as any}
                expandedCategories={expandedCategories}
                toggleCategory={toggleCategory}
                getCategoryBasedValueColor={getCategoryBasedValueColor}
              />
              <PeriodSummary
                selectedPeriod={controller.selectedPeriod || null}
                comparisonPeriod={controller.comparisonPeriod || null}
                groupedData={groupedData as any}
                getPeriodTransactionCount={controller.getPeriodTransactionCount}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};    